// src/components/CustomDialog.tsx
// ALPHA: Custom dialog to replace browser alert/confirm/prompt

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, CheckCircle, HelpCircle, X } from 'lucide-react';
import './CustomDialog.css';

export type DialogType = 'alert' | 'confirm' | 'prompt' | 'menu';
export type DialogVariant = 'info' | 'success' | 'warning' | 'error';

export interface MenuOption {
  id: string;
  label: string;
  icon?: string;
  description?: string;
}

export interface DialogOptions {
  type: DialogType;
  variant?: DialogVariant;
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  defaultValue?: string;
  placeholder?: string;
  menuOptions?: MenuOption[];
  onConfirm?: (value?: string) => void;
  onCancel?: () => void;
}

interface CustomDialogProps {
  isOpen: boolean;
  options: DialogOptions;
  onClose: () => void;
}

const CustomDialog: React.FC<CustomDialogProps> = ({ isOpen, options, onClose }) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState(options.defaultValue || '');
  const [selectedMenuOption, setSelectedMenuOption] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && options.type === 'prompt' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, options.type]);

  useEffect(() => {
    setInputValue(options.defaultValue || '');
  }, [options.defaultValue]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (options.type === 'prompt') {
      options.onConfirm?.(inputValue);
    } else if (options.type === 'menu') {
      options.onConfirm?.(selectedMenuOption || '');
    } else {
      options.onConfirm?.();
    }
    onClose();
  };

  const handleCancel = () => {
    options.onCancel?.();
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && options.type !== 'prompt') {
      e.preventDefault();
      handleConfirm();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleConfirm();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  const getIcon = () => {
    switch (options.variant) {
      case 'success':
        return <CheckCircle size={24} className="dialog-icon success" />;
      case 'error':
      case 'warning':
        return <AlertCircle size={24} className="dialog-icon warning" />;
      default:
        return <HelpCircle size={24} className="dialog-icon info" />;
    }
  };

  return (
    <div className="custom-dialog-overlay" data-testid="custom-dialog-overlay" onClick={handleCancel} onKeyDown={handleKeyDown}>
      <div className="custom-dialog" data-testid="custom-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="custom-dialog-header">
          {getIcon()}
          {options.title && <h3>{options.title}</h3>}
          <button
            type="button"
            className="dialog-close-btn"
            onClick={handleCancel}
            style={{ pointerEvents: 'auto' }}
          >
            <X size={20} />
          </button>
        </div>

        <div className="custom-dialog-body">
          <p>{options.message}</p>
          {options.type === 'prompt' && (
            <input
              ref={inputRef}
              type="text"
              data-testid="custom-dialog-input"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleInputKeyDown}
              placeholder={options.placeholder}
              className="dialog-input"
            />
          )}
          {options.type === 'menu' && options.menuOptions && (
            <div className="dialog-menu-options">
              {options.menuOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  data-testid={`custom-dialog-menu-${option.id}`}
                  className={`dialog-menu-option ${selectedMenuOption === option.id ? 'selected' : ''}`}
                  onClick={() => setSelectedMenuOption(option.id)}
                  style={{ pointerEvents: 'auto' }}
                >
                  {option.icon && <span className="menu-option-icon">{option.icon}</span>}
                  <div className="menu-option-content">
                    <div className="menu-option-label">{option.label}</div>
                    {option.description && (
                      <div className="menu-option-description">{option.description}</div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="custom-dialog-footer">
          {options.type !== 'alert' && (
            <button
              type="button"
              className="dialog-btn dialog-btn-cancel"
              onClick={handleCancel}
              style={{ pointerEvents: 'auto' }}
            >
              {options.cancelText || t('common.cancel')}
            </button>
          )}
          <button
            type="button"
            className="dialog-btn dialog-btn-confirm"
            data-testid="custom-dialog-confirm"
            onClick={handleConfirm}
            style={{ pointerEvents: 'auto' }}
          >
            {options.confirmText || (options.type === 'alert' ? t('common.ok') : t('common.confirm'))}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomDialog;

// Hook per usare il dialog facilmente
let globalShowDialog: ((options: DialogOptions) => Promise<string | boolean | null>) | null = null;

export const useDialog = () => {
  const [dialogState, setDialogState] = useState<{
    isOpen: boolean;
    options: DialogOptions;
    resolve: ((value: string | boolean | null) => void) | null;
  }>({
    isOpen: false,
    options: { type: 'alert', message: '' },
    resolve: null
  });

  const showDialog = (options: DialogOptions): Promise<string | boolean | null> => {
    return new Promise((resolve) => {
      setDialogState({
        isOpen: true,
        options,
        resolve
      });
    });
  };

  globalShowDialog = showDialog;

  const handleClose = () => {
    setDialogState(prev => ({ ...prev, isOpen: false }));
    dialogState.resolve?.(null);
  };

  const handleConfirm = (value?: string) => {
    setDialogState(prev => ({ ...prev, isOpen: false }));
    if (dialogState.options.type === 'prompt') {
      dialogState.resolve?.(value || '');
    } else if (dialogState.options.type === 'menu') {
      dialogState.resolve?.(value || '');
    } else if (dialogState.options.type === 'confirm') {
      dialogState.resolve?.(true);
    } else {
      dialogState.resolve?.(true);
    }
  };

  const handleCancel = () => {
    setDialogState(prev => ({ ...prev, isOpen: false }));
    if (dialogState.options.type === 'confirm') {
      dialogState.resolve?.(false);
    } else {
      dialogState.resolve?.(null);
    }
  };

  const dialogComponent = (
    <CustomDialog
      isOpen={dialogState.isOpen}
      options={{
        ...dialogState.options,
        onConfirm: handleConfirm,
        onCancel: handleCancel
      }}
      onClose={handleClose}
    />
  );

  return { showDialog, dialogComponent };
};

// Funzioni helper per usare globalmente
export const showAlert = (message: string, title?: string, variant?: DialogVariant) => {
  if (!globalShowDialog) return Promise.resolve(true);
  return globalShowDialog({
    type: 'alert',
    message,
    title,
    variant
  });
};

export const showConfirm = (message: string, title?: string, confirmText?: string, cancelText?: string) => {
  if (!globalShowDialog) return Promise.resolve(false);
  return globalShowDialog({
    type: 'confirm',
    message,
    title,
    confirmText,
    cancelText
  });
};

export const showPrompt = (message: string, defaultValue?: string, title?: string, placeholder?: string) => {
  if (!globalShowDialog) return Promise.resolve(null);
  return globalShowDialog({
    type: 'prompt',
    message,
    title,
    defaultValue,
    placeholder
  });
};

export const showMenu = (message: string, options: MenuOption[], title?: string) => {
  if (!globalShowDialog) return Promise.resolve(null);
  return globalShowDialog({
    type: 'menu',
    message,
    title,
    menuOptions: options
  });
};
